/**
 * Lightweight Swiper loader
 * Lazily injects the large Swiper bundle only when a slider is present.
 */
(function swiperDeferredLoader() {
    const currentScript = document.currentScript;
    if (!currentScript || !currentScript.dataset) return;
  
    const swiperSrc = currentScript.dataset.swiperSrc;
    const selectors = currentScript.dataset.swiperSelector || 'swiper-container';
    const rootMargin = currentScript.dataset.swiperRootMargin || '200px';
  
    if (!swiperSrc || !selectors) return;
  
    const selectorList = selectors
      .split(',')
      .map((sel) => sel.trim())
      .filter(Boolean);
  
    let loadPromise = null;
    let mutationObserver = null;
    let isWatchingDynamicContent = false;
  
    const querySliders = () => {
      const nodes = [];
      selectorList.forEach((selector) => {
        nodes.push(...document.querySelectorAll(selector));
      });
      return nodes;
    };
  
    const stopDynamicWatchers = () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
      if (isWatchingDynamicContent) {
        document.removeEventListener('shopify:section:load', deferredEvaluate);
        document.removeEventListener('shopify:section:reorder', deferredEvaluate);
        isWatchingDynamicContent = false;
      }
    };
  
    const loadSwiperBundle = () => {
      if (window.customElements?.get('swiper-container')) {
        stopDynamicWatchers();
        return Promise.resolve();
      }
  
      if (loadPromise) {
        return loadPromise;
      }
  
      loadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = swiperSrc;
        script.async = true;
        script.dataset.swiperBundle = 'true';
        script.addEventListener(
          'load',
          () => {
            stopDynamicWatchers();
            resolve();
          },
          { once: true }
        );
        script.addEventListener(
          'error',
          (error) => {
            loadPromise = null;
            reject(error);
          },
          { once: true }
        );
        document.head.appendChild(script);
      });
  
      return loadPromise;
    };
  
    const isElementVisible = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };
  
    const intersectionHandler = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          loadSwiperBundle();
        }
      });
    };
  
    const watchVisibility = (sliders) => {
      if (!('IntersectionObserver' in window)) {
        loadSwiperBundle();
        return;
      }
  
      const observer = new IntersectionObserver(intersectionHandler, {
        rootMargin,
        threshold: 0.01
      });
      sliders.forEach((slider) => observer.observe(slider));
    };
  
    const evaluateSliders = () => {
      const sliders = querySliders();
      if (!sliders.length) {
        return false;
      }
  
      const visible = sliders.some(isElementVisible);
      if (visible) {
        loadSwiperBundle();
      } else {
        watchVisibility(sliders);
      }
      return true;
    };
  
    const deferredEvaluate = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(evaluateSliders, { timeout: 500 });
      } else {
        window.setTimeout(evaluateSliders, 0);
      }
    };
  
    const watchForDynamicSliders = () => {
      if (mutationObserver || isWatchingDynamicContent) return;
  
      mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof Element)) continue;
            if (
              selectorList.some(
                (selector) => node.matches(selector) || node.querySelector(selector)
              )
            ) {
              if (evaluateSliders()) {
                return;
              }
            }
          }
        }
      });
  
      mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
  
      document.addEventListener('shopify:section:load', deferredEvaluate);
      document.addEventListener('shopify:section:reorder', deferredEvaluate);
      isWatchingDynamicContent = true;
    };
  
    const init = () => {
      if (!evaluateSliders()) {
        watchForDynamicSliders();
      }
    };
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  })();
  
  